/**
 * Motor único de física EMG (criterios PATTERN_CRITERIA + correcciones clínicas).
 * Consumido por JsonReportGenerator (globalFindings).
 */
import { EMGResults, EMGInterpretation } from '../types/clinical';
import { diagnosticPatterns } from '../data/diagnosticPatterns';
import type { SpontaneousActivityData } from '../types/emg';
import { UnifiedEMGResults } from '../types/emg';
import { NCSTestResult } from '../types/ncs';
import {
  CLINICAL_THRESHOLDS,
  CORRECTION_FACTORS,
  PATTERN_CRITERIA,
  DIFFERENTIAL_DIAGNOSES,
  CLINICAL_RECOMMENDATIONS,
  AGE_STRATIFIED_REFERENCES,
  CriterionDefinition,
  PatternCriteria,
  TEMPERATURE_STANDARDS,
  collectNcsIntegratedAlerts,
  isLowerLimbNerveFromName,
  ncsStandardTemperatureC,
  PHYSIOLOGICAL_RANGES,
} from '../data/emgClinicalCriteria';

interface PatternScore {
  id: string;
  score: number;
  matchingCriteria: string[];
}

interface Criterion {
  description: string;
  matched: boolean;
  weight: number;
  severity?: 'mild' | 'moderate' | 'severe';
  specificity?: number;
  confidence?: number;
}

export interface EMGPattern {
  name: string;
  score: number;
  confidence: 'Low' | 'Moderate' | 'High';
  subtype?: string;
  criteria: Criterion[];
  statisticalSignificance: number;
  recommendations?: string[];
  differentials?: string[];
  severity: 'normal' | 'minimal' | 'mild' | 'moderate' | 'severe' | 'very_severe';
  clinicalContext?: string;
}

interface PhysicalFactors {
  temperature?: number;
  height?: number;
  age?: number;
  gender?: 'male' | 'female';
}

interface CriterionEvaluationResult {
  matched: boolean;
  severity?: 'mild' | 'moderate' | 'severe';
  confidence?: number;
  rawValue?: number;
  adjustedValue?: number;
}

/** Forma mínima compatible con EMGNerveRecord del JsonReportGenerator (evita import cíclico). */
export interface EmgNerveRecordLike {
  muscleOrNerveName: string;
  side: 'left' | 'right';
  insertionalActivity: 'normal' | 'increased' | 'decreased' | 'absent' | '';
  spontaneousActivity: {
    fibrillations: boolean | 0 | 1 | 2 | 3 | 4;
    positiveWaves: boolean | 0 | 1 | 2 | 3 | 4;
    fasciculations: boolean;
  };
  motorUnitPotentials: {
    amplitude: number;
    duration: number;
    polyphasia: number;
    amplitudeLabel?: string;
    durationLabel?: string;
    polyphasiaLabel?: string;
    configuration?: string;
    phases?: number;
  };
  recruitmentPattern: 'normal' | 'reduced_incomplete' | 'reduced_complete' | 'early' | 'discrete' | '';
  maxVolitionalActivity?: {
    amplitude?: 'normal' | 'reduced' | 'increased' | 'absent' | '';
    pattern?: 'full' | 'reduced' | 'discrete' | 'early' | 'absent' | '';
    effort?: 'max' | 'submax' | 'poor' | '';
  };
  interpretationNotes?: string;
}

export interface DerivedEmgGlobalSlice {
  overallPattern: 'normal' | 'neuropathic' | 'myopathic' | 'mixed' | 'undetermined';
  severity: 'mild' | 'moderate' | 'severe' | 'undetermined';
}

export function emgNerveRecordToUnified(record: EmgNerveRecordLike): UnifiedEMGResults {
  const fib = record.spontaneousActivity.fibrillations;
  const pos = record.spontaneousActivity.positiveWaves;
  const fasc = record.spontaneousActivity.fasciculations;
  const fibGrade = typeof fib === 'number' ? fib : fib ? 2 : 0;
  const posGrade = typeof pos === 'number' ? pos : pos ? 2 : 0;
  const spontaneousActivityData: SpontaneousActivityData = {
    fibrillations: fibGrade as SpontaneousActivityData['fibrillations'],
    positiveSharpWaves: posGrade as SpontaneousActivityData['positiveSharpWaves'],
    fasciculations: { present: fasc, type: 'undetermined' },
  };
  const parts: string[] = [];
  if (fibGrade > 0) parts.push(`fibrilaciones ${fibGrade}+`);
  if (posGrade > 0) parts.push(`ondas positivas ${posGrade}+`);
  if (fasc) parts.push('fasciculaciones');

  let recruitment: UnifiedEMGResults['recruitment'] = 'normal';
  const effectiveRecruitment = record.maxVolitionalActivity?.pattern === 'reduced'
    ? 'reduced_incomplete'
    : record.maxVolitionalActivity?.pattern === 'discrete' || record.maxVolitionalActivity?.pattern === 'absent'
      ? 'discrete'
      : record.maxVolitionalActivity?.pattern === 'early'
        ? 'early'
        : record.recruitmentPattern;

  switch (effectiveRecruitment) {
    case 'reduced_incomplete':
    case 'reduced_complete':
      recruitment = 'reduced';
      break;
    case 'early':
      recruitment = 'early';
      break;
    case 'discrete':
      recruitment = 'discrete';
      break;
    default:
      recruitment = 'normal';
  }

  let insertional: UnifiedEMGResults['insertionalActivity'] = 'normal';
  if (record.insertionalActivity === 'increased') insertional = 'increased';
  else if (record.insertionalActivity === 'decreased' || record.insertionalActivity === 'absent')
    insertional = 'decreased';

  const amplitude = resolveMupAmplitudeUv(
    record.motorUnitPotentials.amplitude,
    record.motorUnitPotentials.amplitudeLabel,
    record.maxVolitionalActivity?.amplitude
  );
  const duration = resolveMupDurationMs(
    record.motorUnitPotentials.duration,
    record.motorUnitPotentials.durationLabel
  );
  const polyphasia = resolveMupPolyphasiaPercent(
    record.motorUnitPotentials.polyphasia,
    record.motorUnitPotentials.polyphasiaLabel,
    record.motorUnitPotentials.configuration
  );
  const phases = record.motorUnitPotentials.phases ||
    (polyphasia >= 30 ? 5 : polyphasia > 0 ? 4 : 3);

  return {
    muscle: record.muscleOrNerveName || 'unknown',
    side: record.side,
    insertionalActivity: insertional,
    spontaneousActivity: parts.length ? parts.join(', ') : 'sin hallazgos espontáneos significativos',
    spontaneousActivityData,
    motorUnitActionPotentials: {
      amplitude,
      duration,
      polyphasia,
      phases,
      stability: 'stable',
    },
    recruitment,
    interferencePattern: 'normal',
    status: undefined,
    findings: record.interpretationNotes ? [record.interpretationNotes] : undefined,
  };
}

function normalizeQualitativeLabel(value?: string): string {
  return String(value || '').toLowerCase().trim();
}

function resolveMupAmplitudeUv(
  numeric: number,
  label?: string,
  maxVolitionalAmplitude?: string
): number {
  if (numeric > 0) return numeric > 50 ? numeric : numeric * 1000;
  const normalized = normalizeQualitativeLabel(label || maxVolitionalAmplitude);
  if (/\b(increased|aumentad|large|giant)\b/.test(normalized)) return 3000;
  if (/\b(reduced|disminuid|small|low)\b/.test(normalized)) return 300;
  if (/\b(normal)\b/.test(normalized)) return 1000;
  return 0;
}

function resolveMupDurationMs(numeric: number, label?: string): number {
  if (numeric > 0) return numeric;
  const normalized = normalizeQualitativeLabel(label);
  if (/\b(increased|aumentad|long|prolong)\b/.test(normalized)) return 16;
  if (/\b(reduced|disminuid|short)\b/.test(normalized)) return 5;
  if (/\b(normal)\b/.test(normalized)) return 10;
  return 0;
}

function resolveMupPolyphasiaPercent(
  numeric: number,
  label?: string,
  configuration?: string
): number {
  if (numeric > 0) return numeric;
  const normalized = normalizeQualitativeLabel(`${label || ''} ${configuration || ''}`);
  if (/\b(many|abundant|marked|poly|polifas)\b/.test(normalized)) return 35;
  if (/\b(few|escas|rare)\b/.test(normalized)) return 12;
  if (/\b(none|normal|no)\b/.test(normalized)) return 0;
  return 0;
}

export class ClinicalEmgPhysiology {

  /**
   * Resumen narrativo de hallazgos EMG por músculo (UI / reportes).
   * Usa umbrales de {@link CLINICAL_THRESHOLDS}.
   */
  static formatFindingsSummary(emgData: UnifiedEMGResults): string {
    const findings: string[] = [];

    findings.push(this.describeInsertionalActivity(emgData.insertionalActivity));

    const spontaneousLine = this.describeSpontaneousActivity(emgData);
    if (spontaneousLine) findings.push(spontaneousLine);

    const mup = emgData.motorUnitActionPotentials;
    if (mup) {
      findings.push(this.describeMupAmplitude(mup.amplitude));
      findings.push(this.describeMupDuration(mup.duration));
      findings.push(this.describePolyphasia(mup.polyphasia));
    }

    findings.push(this.describeRecruitment(emgData.recruitment));

    return findings.filter(Boolean).join('\n');
  }

  private static normalizeAmplitudeMv(value: number): number {
    return value > 50 ? value / 1000 : value;
  }

  private static describeInsertionalActivity(
    value: UnifiedEMGResults['insertionalActivity'] | string
  ): string {
    switch (value) {
      case 'normal':
        return 'Actividad de inserción normal';
      case 'increased':
      case 'aumentada':
        return 'Actividad de inserción aumentada, sugestivo de irritabilidad muscular';
      case 'decreased':
      case 'disminuida':
        return 'Actividad de inserción disminuida, sugestivo de atrofia muscular';
      case 'absent':
      case 'ausente':
        return 'Actividad de inserción ausente, sugestivo de fibrosis muscular';
      default:
        return 'Actividad de inserción no especificada';
    }
  }

  private static describeSpontaneousActivity(emgData: UnifiedEMGResults): string {
    const data = emgData.spontaneousActivityData;
    if (data) {
      const parts: string[] = [];
      const fib = data.fibrillations;
      const pos = data.positiveSharpWaves;
      if (typeof fib === 'number' ? fib > 0 : fib) parts.push('fibrilaciones');
      if (typeof pos === 'number' ? pos > 0 : pos) parts.push('ondas positivas');
      if (data.fasciculations?.present) parts.push('fasciculaciones');
      if (parts.length === 0) return 'No se observa actividad espontánea anormal';
      return `Se observa actividad espontánea anormal con ${parts.join(', ')}`;
    }

    const text = (emgData.spontaneousActivity || '').toLowerCase();
    if (!text || text === 'none' || text.includes('sin hallazgos')) {
      return 'No se observa actividad espontánea anormal';
    }
    if (
      text.includes('fibrillation') ||
      text.includes('fibrilacion') ||
      text.includes('positive') ||
      text.includes('positiva') ||
      text.includes('fasciculation') ||
      text.includes('fasciculacion')
    ) {
      return `Actividad espontánea anormal: ${emgData.spontaneousActivity}`;
    }
    return 'No se observa actividad espontánea anormal';
  }

  private static describeMupAmplitude(value: number): string {
    const mv = this.normalizeAmplitudeMv(value);
    const neuropathic = CLINICAL_THRESHOLDS.NEUROPATHIC.amplitude.normal;
    const myopathic = CLINICAL_THRESHOLDS.MYOPATHIC.amplitude.normal;
    if (mv > neuropathic) return 'Amplitud aumentada, sugestivo de reinervación';
    if (mv < myopathic) return 'Amplitud disminuida, sugestivo de pérdida de unidades motoras';
    return 'Amplitud normal';
  }

  private static describeMupDuration(value: number): string {
    const neuropathic = CLINICAL_THRESHOLDS.NEUROPATHIC.duration.normal;
    const myopathic = CLINICAL_THRESHOLDS.MYOPATHIC.duration.normal;
    if (value > neuropathic) return 'Duración aumentada, sugestivo de reinervación';
    if (value < myopathic) return 'Duración disminuida, sugestivo de pérdida de unidades motoras';
    return 'Duración normal';
  }

  private static describePolyphasia(value: number): string {
    if (value > CLINICAL_THRESHOLDS.NEUROPATHIC.polyphasia.normal) {
      return 'Polifasia aumentada, sugestivo de reinervación';
    }
    return 'Polifasia normal';
  }

  private static describeRecruitment(value: UnifiedEMGResults['recruitment'] | string): string {
    switch (value) {
      case 'normal':
        return 'Patrón de reclutamiento normal';
      case 'reduced':
      case 'reducido':
        return 'Patrón de reclutamiento reducido, sugestivo de pérdida de unidades motoras';
      case 'early':
      case 'precoz':
        return 'Patrón de reclutamiento precoz, sugestivo de miopatía';
      case 'discrete':
        return 'Patrón de reclutamiento discreto, sugestivo de denervación severa';
      case 'aumentado':
        return 'Patrón de reclutamiento aumentado, sugestivo de debilidad muscular';
      default:
        return 'Patrón de reclutamiento no especificado';
    }
  }

  /**
   * Método principal para analizar patrones EMG con mejoras clínicas
   */
  static analyzeForSpecificPatterns(
    emgData: UnifiedEMGResults,
    physicalFactors?: PhysicalFactors,
    ncsResults?: NCSTestResult[]
  ): EMGPattern[] {
    // Aplicar correcciones por factores físicos (incluyendo edad)
    const correctedData = physicalFactors ? 
      this.applyCorrectionFactors(emgData, physicalFactors.temperature || 32, physicalFactors.height || 170, physicalFactors.age || 30) : 
      emgData;

    const patterns: EMGPattern[] = [];

    // Analizar todos los patrones usando la configuración externalizada
    patterns.push(this.analyzePatternFromCriteria('neuropathic', correctedData, physicalFactors));
    patterns.push(this.analyzePatternFromCriteria('axonal', correctedData, physicalFactors));
    patterns.push(this.analyzePatternFromCriteria('demyelinating', correctedData, physicalFactors));
    patterns.push(this.analyzePatternFromCriteria('myopathic', correctedData, physicalFactors));

    if (ncsResults?.length) {
      const alerts = collectNcsIntegratedAlerts(ncsResults);
      if (alerts.length > 0) {
        patterns.push({
          name: 'Alertas neuroconducción (EAN/PNS / técnica)',
          score: 0.42 + Math.min(0.12, alerts.length * 0.03),
          confidence: alerts.some(a => a.urgency === 'high' || a.urgency === 'urgent')
            ? 'High'
            : 'Moderate',
          criteria: alerts.map(a => ({
            description: a.message,
            matched: true,
            weight: a.urgency === 'high' || a.urgency === 'urgent' ? 2.8 : 2.2,
            specificity: 0.78,
            confidence: 0.72,
          })),
          statisticalSignificance: 0.55,
          recommendations: [
            'Correlacionar con datos segmentarios, temperatura y técnica de estimulación.',
          ],
          severity: 'minimal',
          clinicalContext:
            'Integración automática: duración CMAP vs umbrales por nervio, Martin-Gruber mediano, sural sparing y revisión de polaridad.',
        });
      }
    }

    // Filtrar y ordenar patrones mejorado
    return patterns
      .filter(p => p.score > 0.2) // Umbral más bajo para capturar más patrones sutiles
      .sort((a, b) => {
        // Priorizar primero por significancia estadística, luego por score
        const significanceDiff = b.statisticalSignificance - a.statisticalSignificance;
        if (Math.abs(significanceDiff) > 0.15) return significanceDiff;
        
        const scoreDiff = b.score - a.score;
        if (Math.abs(scoreDiff) > 0.1) return scoreDiff;
        
        // Como desempate, priorizar patrones con mayor número de criterios positivos
        const criteriaCountDiff = b.criteria.filter(c => c.matched).length - a.criteria.filter(c => c.matched).length;
        return criteriaCountDiff;
      });
  }

  /**
   * Aplica factores de corrección basados en temperatura, altura y edad.
   * Temperatura: estándar 32 °C (MMSS) / 30 °C (MMII) según etiqueta de músculo/nervio.
   */
  private static applyCorrectionFactors(
    emgData: UnifiedEMGResults,
    temperature: number,
    height: number,
    age: number
  ): UnifiedEMGResults {
    const limbStandardTemp = ncsStandardTemperatureC(emgData.muscle || 'median');

    const correctedMup: any = {
      ...emgData,
      motorUnitActionPotentials: emgData.motorUnitActionPotentials
        ? { ...emgData.motorUnitActionPotentials }
        : emgData.motorUnitActionPotentials,
    };

    if (temperature < limbStandardTemp) {
      const tempDifference = limbStandardTemp - temperature;

      if (correctedMup.conductionVelocity) {
        correctedMup.conductionVelocity *= 1 + 0.05 * tempDifference;
      }

      const amplitudeCorrection = Math.max(
        0.4,
        1 - TEMPERATURE_STANDARDS.AMPLITUDE_CORRECTION_FACTOR * tempDifference
      );
      if (correctedMup.motorUnitActionPotentials?.amplitude) {
        correctedMup.motorUnitActionPotentials.amplitude *= amplitudeCorrection;
      }
      if (typeof correctedMup.amplitude === 'number') {
        correctedMup.amplitude *= amplitudeCorrection;
      }

      if (correctedMup.motorUnitActionPotentials?.duration) {
        const durFactor = 1 - 0.024 * tempDifference;
        correctedMup.motorUnitActionPotentials.duration = Math.max(
          0.1,
          correctedMup.motorUnitActionPotentials.duration * durFactor
        );
      }

      if (correctedMup.distalLatency) {
        const correctionFactor = 2.4 * tempDifference / 100;
        correctedMup.distalLatency = Math.max(
          0.1,
          correctedMup.distalLatency * (1 - correctionFactor)
        );
      }

      if (correctedMup.sensoryLatency) {
        const correctionFactor = 1.8 * tempDifference / 100;
        correctedMup.sensoryLatency = Math.max(
          0.1,
          correctedMup.sensoryLatency * (1 - correctionFactor)
        );
      }

      if (correctedMup.fWaveLatency) {
        const fWaveCorrectionFactor = 3.2 * tempDifference / 100;
        correctedMup.fWaveLatency = Math.max(
          0.1,
          correctedMup.fWaveLatency * (1 - fWaveCorrectionFactor)
        );
      }

      correctedMup.validityNote = {
        level: 'unreliable' as const,
        message: `Temperatura de ${temperature}°C por debajo del estándar (${limbStandardTemp}°C). Los datos de amplitud pueden estar artificialmente elevados y las latencias/velocidades falsamente anómalas.`,
      };

      console.log(`🌡️ Corrección por temperatura: ${tempDifference}°C bajo estándar territorial (${limbStandardTemp}°C)`);
    }
    
    // 🔥 CORRECCIÓN POR ALTURA - PRINCIPIOS NEUROFISIOLÓGICOS
    // Altura estándar: 170 cm
    // Corrección de 0.5 ms por cada 10 cm de diferencia en altura
    const standardHeight = 170; // cm
    
    if (Math.abs(height - standardHeight) > 5) { // Solo aplicar si la diferencia es significativa
      const heightDifference = height - standardHeight;
      const heightCorrectionMs = (heightDifference / 10) * 0.5; // 0.5ms por cada 10cm
      
      // Aplicar corrección a latencias
      if (correctedMup.distalLatency) {
        correctedMup.distalLatency = correctedMup.distalLatency - heightCorrectionMs;
        correctedMup.distalLatency = Math.max(0.1, correctedMup.distalLatency);
      }
      
      if (correctedMup.sensoryLatency) {
        correctedMup.sensoryLatency = correctedMup.sensoryLatency - heightCorrectionMs;
        correctedMup.sensoryLatency = Math.max(0.1, correctedMup.sensoryLatency);
      }
      
      if (correctedMup.fWaveLatency) {
        correctedMup.fWaveLatency = correctedMup.fWaveLatency - (heightCorrectionMs * 2); // Doble corrección para ondas F
        correctedMup.fWaveLatency = Math.max(0.1, correctedMup.fWaveLatency);
      }
      
      console.log(`📏 Corrección por altura aplicada: ${heightDifference}cm (${heightCorrectionMs}ms)`);
    }
    
    // 🔥 CORRECCIÓN POR EDAD - PRINCIPIOS NEUROFISIOLÓGICOS
    // Edad estándar: 30 años
    // Incremento de 0.2ms por cada 10 años después de los 30
    const standardAge = 30;
    
    if (age > standardAge) {
      const ageDifference = age - standardAge;
      const ageCorrectionMs = (ageDifference / 10) * 0.2; // 0.2ms por cada 10 años
      
      // Aplicar corrección a latencias (aumentan con la edad)
      if (correctedMup.distalLatency) {
        correctedMup.distalLatency = correctedMup.distalLatency - ageCorrectionMs;
        correctedMup.distalLatency = Math.max(0.1, correctedMup.distalLatency);
      }
      
      if (correctedMup.sensoryLatency) {
        correctedMup.sensoryLatency = correctedMup.sensoryLatency - ageCorrectionMs;
        correctedMup.sensoryLatency = Math.max(0.1, correctedMup.sensoryLatency);
      }
      
      // Corrección para velocidad de conducción (disminuye con la edad)
      if (correctedMup.conductionVelocity) {
        const velocityAgeCorrectionFactor = (ageDifference / 10) * 0.02; // 2% por cada 10 años
        correctedMup.conductionVelocity = correctedMup.conductionVelocity * (1 + velocityAgeCorrectionFactor);
      }
      
      console.log(`👴 Corrección por edad aplicada: ${ageDifference} años (${ageCorrectionMs}ms)`);
    }
    
    correctedMup.correctionMetadata = {
      originalTemperature: temperature,
      standardTemperature: limbStandardTemp,
      originalHeight: height,
      standardHeight,
      originalAge: age,
      standardAge,
      correctionApplied: true,
      correctionTimestamp: new Date().toISOString(),
    };

    return correctedMup as UnifiedEMGResults;
  }

  /**
   * NUEVO: Método genérico para analizar patrones usando configuración externalizada
   */
  private static analyzePatternFromCriteria(
    patternType: keyof PatternCriteria, 
    emgData: UnifiedEMGResults, 
    physicalFactors?: PhysicalFactors
  ): EMGPattern {
    const criteriaDefinitions = PATTERN_CRITERIA[patternType];
    const evaluatedCriteria: Criterion[] = [];

    // Evaluar cada criterio
    for (const criterionDef of criteriaDefinitions) {
      const evaluation = this.evaluateCriterion(criterionDef, emgData, physicalFactors);
      
      evaluatedCriteria.push({
        description: criterionDef.description,
        matched: evaluation.matched,
        weight: criterionDef.weight,
        severity: evaluation.severity,
        specificity: criterionDef.specificity,
        confidence: evaluation.confidence
      });
    }

    // Calcular score ponderado mejorado
    const weightedScore = this.calculateEnhancedWeightedScore(evaluatedCriteria);
    
    // Calcular significancia estadística mejorada
    const statisticalSignificance = this.calculateEnhancedStatisticalSignificance(evaluatedCriteria);
    
    // Determinar subtipo específico
    const subtype = this.determineSubtype(patternType, evaluatedCriteria, emgData);
    
    // Determinar severidad basada en criterios clínicos
    const severity = this.determineEnhancedSeverity(evaluatedCriteria, patternType);
    
    // Generar recomendaciones específicas y accionables
    const recommendations = this.generateEnhancedRecommendations(patternType, subtype, severity);
    
    // Generar diagnósticos diferenciales enriquecidos
    const differentials = this.generateEnhancedDifferentials(patternType, subtype);
    
    // Generar contexto clínico
    const clinicalContext = this.generateClinicalContext(patternType, evaluatedCriteria, physicalFactors, emgData);

    return {
      name: this.formatPatternName(patternType, subtype),
      score: weightedScore,
      confidence: this.determineConfidenceLevel(weightedScore, statisticalSignificance),
      subtype,
      criteria: evaluatedCriteria,
      statisticalSignificance,
      recommendations,
      differentials,
      severity,
      clinicalContext
    };
  }

  /**
   * NUEVO: Sistema de evaluación de criterios modulares
   */
  private static evaluateCriterion(
    criterionDef: CriterionDefinition, 
    emgData: UnifiedEMGResults, 
    physicalFactors?: PhysicalFactors
  ): CriterionEvaluationResult {
    
    switch (criterionDef.evaluationFunction) {
      case 'evaluateNeuropathicDuration':
        return this.evaluateNeuropathicDuration(emgData, criterionDef, physicalFactors);
      case 'evaluateNeuropathicAmplitude':
        return this.evaluateNeuropathicAmplitude(emgData, criterionDef, physicalFactors);
      case 'evaluateReducedRecruitment':
        return this.evaluateReducedRecruitment(emgData, criterionDef);
      case 'evaluateFibrillations':
        return this.evaluateFibrillations(emgData, criterionDef);
      case 'evaluatePositiveWaves':
        return this.evaluatePositiveWaves(emgData, criterionDef);
      case 'evaluateComplexMUPs':
        return this.evaluateComplexMUPs(emgData, criterionDef);
      case 'evaluateReducedCMAPAmplitude':
        return this.evaluateReducedCMAPAmplitude(emgData, criterionDef);
      case 'evaluatePreservedVelocity':
        return this.evaluatePreservedVelocity(emgData, criterionDef);
      case 'evaluateActiveDenervation':
        return this.evaluateActiveDenervation(emgData, criterionDef);
      case 'evaluateReducedSNAPAmplitude':
        return this.evaluateReducedSNAPAmplitude(emgData, criterionDef);
      case 'evaluateReducedVelocity':
        return this.evaluateReducedVelocity(emgData, criterionDef);
      case 'evaluateProlongedLatency':
        return this.evaluateProlongedLatency(emgData, criterionDef);
      case 'evaluateConductionBlock':
        return this.evaluateConductionBlock(emgData, criterionDef);
      case 'evaluateTemporalDispersion':
        return this.evaluateTemporalDispersion(emgData, criterionDef);
      case 'evaluateFWaveAbnormalities':
        return this.evaluateFWaveAbnormalities(emgData, criterionDef);
      case 'evaluateDecreasedDuration':
        return this.evaluateDecreasedDuration(emgData, criterionDef, physicalFactors);
      case 'evaluateDecreasedAmplitude':
        return this.evaluateDecreasedAmplitude(emgData, criterionDef, physicalFactors);
      case 'evaluateEarlyRecruitment':
        return this.evaluateEarlyRecruitment(emgData, criterionDef);
      case 'evaluateIncreasedPolyphasia':
        return this.evaluateIncreasedPolyphasia(emgData, criterionDef);
      case 'evaluateMyopathicRecruitment':
        return this.evaluateMyopathicRecruitment(emgData, criterionDef);
      default:
        console.warn(`Función de evaluación no encontrada: ${criterionDef.evaluationFunction}`);
        return { matched: false };
    }
  }

  // ============ FUNCIONES DE EVALUACIÓN ESPECÍFICAS ============

  private static evaluateNeuropathicDuration(
    emgData: UnifiedEMGResults, 
    criterionDef: CriterionDefinition, 
    physicalFactors?: PhysicalFactors
  ): CriterionEvaluationResult {
    const duration = emgData.motorUnitActionPotentials.duration;
    const threshold = this.getAgeAdjustedThreshold('neuropathic', 'duration', physicalFactors?.age);
    
    if (duration > threshold.severe) {
      return { matched: true, severity: 'severe', confidence: 0.9, rawValue: duration, adjustedValue: threshold.severe };
    } else if (duration > threshold.normal) {
      return { matched: true, severity: 'moderate', confidence: 0.7, rawValue: duration, adjustedValue: threshold.normal };
    }
    
    return { matched: false, rawValue: duration };
  }

  private static evaluateNeuropathicAmplitude(
    emgData: UnifiedEMGResults, 
    criterionDef: CriterionDefinition, 
    physicalFactors?: PhysicalFactors
  ): CriterionEvaluationResult {
    const amplitude = emgData.motorUnitActionPotentials.amplitude;
    const threshold = this.getAgeAdjustedThreshold('neuropathic', 'amplitude', physicalFactors?.age);
    
    if (amplitude > threshold.severe) {
      return { matched: true, severity: 'severe', confidence: 0.85, rawValue: amplitude };
    } else if (amplitude > threshold.normal) {
      return { matched: true, severity: 'moderate', confidence: 0.7, rawValue: amplitude };
    }
    
    return { matched: false, rawValue: amplitude };
  }

  private static evaluateReducedRecruitment(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const recruitment = emgData.recruitment;
    // CORREGIDO: Solo 'reduced' es match para patrón neuropático.
    // 'normal' NO debe contar como reclutamiento reducido.
    const matched = recruitment === 'reduced';
    const severity: 'severe' | 'moderate' = 'moderate'; // reduced es al menos moderado
    
    return { 
      matched, 
      severity: matched ? severity : undefined, 
      confidence: matched ? 0.8 : 0 
    };
  }

  private static evaluateFibrillations(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const hasFibrillations = emgData.spontaneousActivity.includes('Fibrillations') || 
                            emgData.spontaneousActivity.includes('fibrillations') ||
                            emgData.spontaneousActivity.includes('Fibrilaciones');
    
    return { 
      matched: hasFibrillations, 
      confidence: hasFibrillations ? 0.75 : 0 
    };
  }

  private static evaluatePositiveWaves(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const hasPositiveWaves = emgData.spontaneousActivity.includes('Positive Waves') || 
                            emgData.spontaneousActivity.includes('positive waves') ||
                            emgData.spontaneousActivity.includes('Ondas Positivas');
    
    return { 
      matched: hasPositiveWaves, 
      confidence: hasPositiveWaves ? 0.75 : 0 
    };
  }

  private static evaluateComplexMUPs(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const duration = emgData.motorUnitActionPotentials.duration;
    const amplitude = emgData.motorUnitActionPotentials.amplitude;
    const polyphasia = emgData.motorUnitActionPotentials.polyphasia;
    
    const isComplex = duration > 12 && amplitude > 4 && polyphasia > 25;
    const severity = (duration > 15 && amplitude > 6 && polyphasia > 35) ? 'severe' : 'moderate';
    
    return { 
      matched: isComplex, 
      severity: isComplex ? severity : undefined, 
      confidence: isComplex ? 0.9 : 0 
    };
  }

  private static evaluateReducedCMAPAmplitude(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    // Buscar datos de NCS en la estructura de datos
    const ncsData = (emgData as any).ncsResults || (emgData as any).conductionStudy;
    if (!ncsData?.amplitude) return { matched: false };
    
    const amplitude = ncsData.amplitude;
    const threshold = CLINICAL_THRESHOLDS.NCS.axonal.cMAPAmplitude;
    
    const matched = amplitude < threshold;
    const severity = amplitude < threshold * 0.5 ? 'severe' : 'moderate';
    
    return { 
      matched, 
      severity: matched ? severity : undefined, 
      confidence: matched ? 0.85 : 0,
      rawValue: amplitude 
    };
  }

  private static evaluatePreservedVelocity(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const ncsData = (emgData as any).ncsResults || (emgData as any).conductionStudy;
    if (!ncsData?.velocity) return { matched: false };
    
    const velocity = ncsData.velocity;
    const threshold = CLINICAL_THRESHOLDS.NCS.axonal.conductionVelocity;
    
    const matched = velocity >= threshold;
    
    return { 
      matched, 
      confidence: matched ? 0.75 : 0,
      rawValue: velocity 
    };
  }

  private static evaluateActiveDenervation(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const hasFibrillations = emgData.spontaneousActivity.includes('Fibrillations') || 
                            emgData.spontaneousActivity.includes('fibrillations');
    const hasPositiveWaves = emgData.spontaneousActivity.includes('Positive Waves') || 
                            emgData.spontaneousActivity.includes('positive waves');
    
    const matched = hasFibrillations && hasPositiveWaves;
    
    return { 
      matched, 
      confidence: matched ? 0.8 : 0 
    };
  }

  private static evaluateReducedSNAPAmplitude(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const ncsData = (emgData as any).ncsResults || (emgData as any).conductionStudy;
    if (!ncsData?.sensorAmplitude) return { matched: false };
    
    const amplitude = ncsData.sensorAmplitude;
    const threshold = CLINICAL_THRESHOLDS.NCS.axonal.sNAPAmplitude;
    
    const matched = amplitude < threshold;
    const severity = amplitude < threshold * 0.3 ? 'severe' : 'moderate';
    
    return { 
      matched, 
      severity: matched ? severity : undefined, 
      confidence: matched ? 0.82 : 0,
      rawValue: amplitude 
    };
  }

  private static evaluateReducedVelocity(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const ncsData = (emgData as any).ncsResults || (emgData as any).conductionStudy;
    if (!ncsData?.velocity) return { matched: false };
    
    const velocity = ncsData.velocity;
    const threshold = CLINICAL_THRESHOLDS.NCS.demyelinating.velocityReduction;
    
    const matched = velocity < threshold;
    const severity = velocity < threshold * 0.8 ? 'severe' : 'moderate';
    
    return { 
      matched, 
      severity: matched ? severity : undefined, 
      confidence: matched ? 0.9 : 0,
      rawValue: velocity 
    };
  }

  private static evaluateProlongedLatency(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const ncsData = (emgData as any).ncsResults || (emgData as any).conductionStudy;
    if (!ncsData?.latency) return { matched: false };
    
    const latency = ncsData.latency;
    const threshold = CLINICAL_THRESHOLDS.NCS.demyelinating.latencyProlongation;
    
    const matched = latency > threshold;
    const severity = latency > threshold * 1.5 ? 'severe' : 'moderate';
    
    return { 
      matched, 
      severity: matched ? severity : undefined, 
      confidence: matched ? 0.85 : 0,
      rawValue: latency 
    };
  }

  private static evaluateConductionBlock(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const ncsData = (emgData as any).ncsResults || (emgData as any).conductionStudy;
    if (!ncsData?.conductionBlock) return { matched: false };
    
    const blockPercentage = ncsData.conductionBlock;
    const threshold = CLINICAL_THRESHOLDS.NCS.demyelinating.conductionBlock;
    
    const matched = blockPercentage >= threshold;
    const severity = blockPercentage >= threshold * 1.5 ? 'severe' : 'moderate';
    
    return { 
      matched, 
      severity: matched ? severity : undefined, 
      confidence: matched ? 0.95 : 0, // Mayor confianza - muy específico
      rawValue: blockPercentage 
    };
  }

  private static evaluateTemporalDispersion(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const ncsData = (emgData as any).ncsResults || (emgData as any).conductionStudy;
    if (!ncsData?.temporalDispersion) return { matched: false };
    
    const dispersion = ncsData.temporalDispersion;
    const threshold = CLINICAL_THRESHOLDS.NCS.demyelinating.temporalDispersion;
    
    const matched = dispersion >= threshold;
    
    return { 
      matched, 
      confidence: matched ? 0.88 : 0,
      rawValue: dispersion 
    };
  }

  private static evaluateFWaveAbnormalities(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const ncsData = (emgData as any).ncsResults || (emgData as any).conductionStudy;
    if (!ncsData?.fWaveLatency) return { matched: false };

    const fWaveLatency = ncsData.fWaveLatency;
    const late = PHYSIOLOGICAL_RANGES.ncs.lateResponses;
    const lowerLimb = isLowerLimbNerveFromName(emgData.muscle || '');
    const range = lowerLimb ? late.fWaveLatencyLowerLimb : late.fWaveLatencyUpperLimb;
    const absentOrZero = fWaveLatency === 0;
    const outOfRange = fWaveLatency < range.min || fWaveLatency > range.max;
    const isAbnormal = absentOrZero || outOfRange;

    return {
      matched: isAbnormal,
      confidence: isAbnormal ? 0.75 : 0,
      rawValue: fWaveLatency,
    };
  }

  private static evaluateDecreasedDuration(
    emgData: UnifiedEMGResults, 
    criterionDef: CriterionDefinition, 
    physicalFactors?: PhysicalFactors
  ): CriterionEvaluationResult {
    const duration = emgData.motorUnitActionPotentials.duration;
    const threshold = this.getAgeAdjustedThreshold('myopathic', 'duration', physicalFactors?.age);
    if (!duration || duration <= 0) {
      return { matched: false, rawValue: duration };
    }
    
    if (duration < threshold.severe) {
      return { matched: true, severity: 'severe', confidence: 0.8, rawValue: duration };
    } else if (duration < threshold.normal) {
      return { matched: true, severity: 'moderate', confidence: 0.7, rawValue: duration };
    }
    
    return { matched: false, rawValue: duration };
  }

  private static evaluateDecreasedAmplitude(
    emgData: UnifiedEMGResults, 
    criterionDef: CriterionDefinition, 
    physicalFactors?: PhysicalFactors
  ): CriterionEvaluationResult {
    const amplitude = emgData.motorUnitActionPotentials.amplitude;
    const threshold = this.getAgeAdjustedThreshold('myopathic', 'amplitude', physicalFactors?.age);
    if (!amplitude || amplitude <= 0) {
      return { matched: false, rawValue: amplitude };
    }
    
    if (amplitude < threshold.severe) {
      return { matched: true, severity: 'severe', confidence: 0.75, rawValue: amplitude };
    } else if (amplitude < threshold.normal) {
      return { matched: true, severity: 'moderate', confidence: 0.6, rawValue: amplitude };
    }
    
    return { matched: false, rawValue: amplitude };
  }

  private static evaluateEarlyRecruitment(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const recruitment = emgData.recruitment;
    const matched = recruitment === 'early';
    
    return { 
      matched, 
      confidence: matched ? 0.7 : 0 
    };
  }

  private static evaluateIncreasedPolyphasia(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const polyphasia = emgData.motorUnitActionPotentials.polyphasia;
    const threshold = CLINICAL_THRESHOLDS.MYOPATHIC.polyphasia.normal;
    
    const matched = polyphasia > threshold;
    const severity = polyphasia > CLINICAL_THRESHOLDS.MYOPATHIC.polyphasia.severe ? 'severe' : 'moderate';
    
    return { 
      matched, 
      severity: matched ? severity : undefined, 
      confidence: matched ? 0.9 : 0,
      rawValue: polyphasia 
    };
  }

  private static evaluateMyopathicRecruitment(emgData: UnifiedEMGResults, criterionDef: CriterionDefinition): CriterionEvaluationResult {
    const recruitment = emgData.recruitment;
    // CORREGIDO: Solo 'early' es un valor válido. 'rapid' no existe en el tipo de reclutamiento.
    const matched = recruitment === 'early';
    
    return { 
      matched, 
      confidence: matched ? 0.85 : 0 
    };
  }

  // ============ FUNCIONES DE CÁLCULO MEJORADAS ============

  /**
   * MEJORADO: Cálculo de score ponderado con consideración de confianza individual
   */
  private static calculateEnhancedWeightedScore(criteria: Criterion[]): number {
    const totalWeight = criteria.reduce((sum, c) => sum + c.weight, 0);
    if (totalWeight === 0) return 0;

    const weightedScore = criteria.reduce((sum, c) => {
      if (c.matched) {
        // Incorporar la confianza individual del criterio
        const confidenceMultiplier = c.confidence || 1.0;
        return sum + (c.weight * confidenceMultiplier);
      }
      return sum;
    }, 0) / totalWeight;

    return Math.min(1.0, weightedScore); // Límite superior de 1.0
  }

  /**
   * MEJORADO: Cálculo de significancia estadística con mejor modelo bayesiano
   */
  private static calculateEnhancedStatisticalSignificance(criteria: Criterion[]): number {
    const matchedCriteria = criteria.filter(c => c.matched);
    if (matchedCriteria.length === 0) return 0;

    // Cálculo de especificidad promedio ponderada
    const weightedSpecificity = matchedCriteria.reduce((sum, c) => {
      const specificity = c.specificity || 0.5;
      const confidence = c.confidence || 1.0;
      return sum + (specificity * c.weight * confidence);
    }, 0) / matchedCriteria.reduce((sum, c) => sum + c.weight * (c.confidence || 1.0), 0);

    // Modelo bayesiano mejorado
    const priorProbability = 0.25; // Base probability ajustada
    const likelihood = weightedSpecificity;
    
    // Bonus por número de criterios independientes confirmados
    const criteriaCountBonus = Math.min(0.2, matchedCriteria.length * 0.05);
    
    const posteriorProbability = (likelihood * priorProbability) / 
        (likelihood * priorProbability + (1 - likelihood) * (1 - priorProbability));

    return Math.min(1.0, posteriorProbability + criteriaCountBonus);
  }

  /**
   * NUEVO: Obtener umbrales ajustados por edad
   */
  private static getAgeAdjustedThreshold(
    patternType: 'neuropathic' | 'myopathic', 
    parameter: 'duration' | 'amplitude', 
    age?: number
  ): { normal: number; severe: number } {
    const baseThresholds = CLINICAL_THRESHOLDS[patternType.toUpperCase() as 'NEUROPATHIC' | 'MYOPATHIC'][parameter];
    
    if (!age || !baseThresholds.ageAdjusted) {
      return { normal: baseThresholds.normal, severe: baseThresholds.severe };
    }

    // Determinar grupo etario
    let ageGroup: 'young' | 'middle' | 'older';
    if (age < 40) ageGroup = 'young';
    else if (age < 60) ageGroup = 'middle';
    else ageGroup = 'older';

    const ageAdjusted = baseThresholds.ageAdjusted[ageGroup];
    
    return {
      normal: ageAdjusted.max,
      severe: baseThresholds.severe // Mantener el umbral severo base
    };
  }

  /**
   * MEJORADO: Determinación de subtipo con lógica clínica específica
   */
  private static determineSubtype(
    patternType: keyof PatternCriteria, 
    criteria: Criterion[], 
    emgData: UnifiedEMGResults
  ): string {
    const matchedCriteria = criteria.filter(c => c.matched);
    
    switch (patternType) {
      case 'neuropathic':
        const hasDenervation = matchedCriteria.some(c => 
          c.description.includes('fibrillations') || c.description.includes('positive'));
        const hasReinnervation = matchedCriteria.some(c => 
          c.description.includes('duration') || c.description.includes('amplitude'));
        
        if (hasDenervation && hasReinnervation) return 'Acute on chronic denervation';
        if (hasDenervation) return 'Active denervation pattern';
        if (hasReinnervation) return 'Chronic reinnervation pattern';
        return 'General neuropathic pattern';

      case 'axonal':
        const hasMotorAxonal = matchedCriteria.some(c => c.description.includes('CMAP'));
        const hasSensoryAxonal = matchedCriteria.some(c => c.description.includes('SNAP'));
        
        if (hasMotorAxonal && hasSensoryAxonal) return 'Sensorimotor axonal';
        if (hasMotorAxonal) return 'Motor axonal';
        if (hasSensoryAxonal) return 'Sensory axonal';
        return 'Axonal pattern';

      case 'demyelinating':
        const hasConductionBlock = matchedCriteria.some(c => c.description.includes('block'));
        const hasSlowing = matchedCriteria.some(c => c.description.includes('velocity'));
        
        if (hasConductionBlock) return 'With conduction block';
        if (hasSlowing) return 'Primary demyelination';
        return 'Demyelinating pattern';

      case 'myopathic':
        const severity = this.determineEnhancedSeverity(criteria, patternType);
        if (severity === 'severe' || severity === 'very_severe') return 'Severe myopathic';
        return 'Myopathic pattern';

      default:
        return '';
    }
  }

  /**
   * MEJORADO: Determinación de severidad con criterios clínicos específicos
   */
  private static determineEnhancedSeverity(
    criteria: Criterion[], 
    patternType: keyof PatternCriteria
  ): 'normal' | 'minimal' | 'mild' | 'moderate' | 'severe' | 'very_severe' {
    const matchedCriteria = criteria.filter(c => c.matched);
    const severeCriteria = criteria.filter(c => c.severity === 'severe');
    const moderateCriteria = criteria.filter(c => c.severity === 'moderate');

    // Criterios específicos por tipo de patrón
    switch (patternType) {
      case 'neuropathic':
        // Para neuropático, la presencia de bloqueo o denervación aguda es severa
        const hasActiveDenervation = matchedCriteria.some(c => 
          c.description.includes('fibrillations') && c.description.includes('positive'));
        if (hasActiveDenervation && severeCriteria.length >= 1) return 'very_severe';
        if (hasActiveDenervation || severeCriteria.length >= 2) return 'severe';
        break;

      case 'demyelinating':
        // Bloqueo de conducción es criterio de severidad crítico
        const hasConductionBlock = matchedCriteria.some(c => c.description.includes('block'));
        if (hasConductionBlock) return 'severe';
        break;

      case 'axonal':
        // Pérdida severa de amplitud indica severidad alta
        const hasMarkedAmplitudeLoss = severeCriteria.some(c => c.description.includes('amplitude'));
        if (hasMarkedAmplitudeLoss) return 'severe';
        break;

      case 'myopathic':
        // Para miopatía, múltiples criterios positivos indican severidad
        if (severeCriteria.length >= 2) return 'severe';
        break;
    }

    // Lógica general de severidad
    if (severeCriteria.length >= 2) return 'very_severe';
    if (severeCriteria.length === 1) return 'severe';
    if (moderateCriteria.length >= 2) return 'moderate';
    if (moderateCriteria.length === 1) return 'mild';
    if (matchedCriteria.length > 0) return 'minimal';
    return 'normal';
  }

  /**
   * MEJORADO: Generación de recomendaciones específicas y accionables
   */
  private static generateEnhancedRecommendations(
    patternType: keyof PatternCriteria, 
    subtype: string, 
    severity: string
  ): string[] {
    const recommendations: string[] = [];
    
    // Obtener recomendaciones base del archivo de configuración
    const baseRecommendations = CLINICAL_RECOMMENDATIONS[patternType];
    
    if (typeof baseRecommendations === 'object' && !Array.isArray(baseRecommendations)) {
      // Para patrones neuropáticos con subtipos
      if (subtype.includes('Active denervation')) {
        recommendations.push(...baseRecommendations.acute_denervation || []);
      } else if (subtype.includes('Chronic reinnervation')) {
        recommendations.push(...baseRecommendations.chronic_reinnervation || []);
      } else {
        recommendations.push(...baseRecommendations.general || []);
      }
    } else if (Array.isArray(baseRecommendations)) {
      recommendations.push(...baseRecommendations);
    }

    // Agregar recomendaciones específicas por severidad
    if (severity === 'very_severe' || severity === 'severe') {
      recommendations.unshift('URGENTE: Evaluación neurológica inmediata recomendada');
      recommendations.push('Considerar hospitalización para monitoreo si hay compromiso respiratorio');
    }

    // Recomendaciones específicas por subtipo
    if (subtype.includes('conduction block')) {
      recommendations.push('Evaluar para neuropatía multifocal motora o PDIC');
      recommendations.push('Considerar inmunoglobulina IV o plasmaféresis');
    }

    return [...new Set(recommendations)]; // Eliminar duplicados
  }

  /**
   * MEJORADO: Generación de diagnósticos diferenciales enriquecidos
   */
  private static generateEnhancedDifferentials(
    patternType: keyof PatternCriteria, 
    subtype: string
  ): string[] {
    const differentials: string[] = [];
    
    const baseDifferentials = DIFFERENTIAL_DIAGNOSES[patternType];
    
    if (typeof baseDifferentials === 'object' && !Array.isArray(baseDifferentials)) {
      // Para patrones neuropáticos
      if (subtype.includes('Active denervation')) {
        differentials.push(...baseDifferentials.acute_denervation || []);
      } else if (subtype.includes('Chronic reinnervation')) {
        differentials.push(...baseDifferentials.chronic_reinnervation || []);
      } else {
        differentials.push(...baseDifferentials.general || []);
      }
    } else if (Array.isArray(baseDifferentials)) {
      differentials.push(...baseDifferentials);
    }

    return [...new Set(differentials)]; // Eliminar duplicados
  }

  /**
   * NUEVO: Generación de contexto clínico
   */
  private static generateClinicalContext(
    patternType: keyof PatternCriteria,
    criteria: Criterion[],
    physicalFactors?: PhysicalFactors,
    emgData?: UnifiedEMGResults
  ): string {
    const matchedCount = criteria.filter(c => c.matched).length;
    const totalCount = criteria.length;
    const percentage = Math.round((matchedCount / totalCount) * 100);

    let context = `Análisis basado en ${matchedCount}/${totalCount} criterios positivos (${percentage}%).`;

    if (physicalFactors?.age) {
      context += ` Valores ajustados para edad de ${physicalFactors.age} años.`;
    }

    const t = physicalFactors?.temperature;
    if (t !== undefined && emgData?.muscle) {
      const std = ncsStandardTemperatureC(emgData.muscle);
      if (t < std) {
        context += ` Corrección aplicada por temperatura ${t}°C (estándar territorial ${std}°C).`;
      }
    }

    return context;
  }

  /**
   * MEJORADO: Formatear nombre del patrón
   */
  private static formatPatternName(patternType: keyof PatternCriteria, subtype: string): string {
    const baseNames = {
      neuropathic: 'Patrón Neuropático',
      axonal: 'Patrón Axonal',
      demyelinating: 'Patrón Desmielinizante',
      myopathic: 'Patrón Miopático'
    };

    const baseName = baseNames[patternType] || patternType;
    return subtype ? `${baseName} (${subtype})` : baseName;
  }

  /**
   * MEJORADO: Determinación de nivel de confianza
   */
  private static determineConfidenceLevel(score: number, statisticalSignificance: number): 'Low' | 'Moderate' | 'High' {
    // Algoritmo mejorado que considera tanto score como significancia
    const combinedScore = (score * 0.6) + (statisticalSignificance * 0.4);
    
    if (combinedScore > 0.75) return 'High';
    if (combinedScore > 0.45) return 'Moderate';
    return 'Low';
  }

  /**
   * Mantener método legacy para compatibilidad
   */
  static generatePatternDescription(patternId: string, matchingCriteria: string[], severity: string): string {
    const pattern = diagnosticPatterns[patternId];
    if (!pattern) return '';
    
    let description = `Los hallazgos electromiográficos son ${
      matchingCriteria.length > pattern.keyFindings.length / 2 ? 'muy compatibles' : 'sugestivos'
    } con ${pattern.name}, `;
    
    description += `una ${pattern.description}, de ${severity}.`;
    
    return description;
  }
}

function mapPhysiologySeverityToGlobal(
  s: EMGPattern['severity']
): DerivedEmgGlobalSlice['severity'] {
  if (s === 'normal' || s === 'minimal') return 'mild';
  if (s === 'mild') return 'mild';
  if (s === 'moderate') return 'moderate';
  if (s === 'severe' || s === 'very_severe') return 'severe';
  return 'undetermined';
}

function severityRankGlobal(s: DerivedEmgGlobalSlice['severity']): number {
  switch (s) {
    case 'severe':
      return 3;
    case 'moderate':
      return 2;
    case 'mild':
      return 1;
    default:
      return 0;
  }
}

/**
 * Agrega la física EMG por registro muscular para alimentar un único globalFindings del ClinicalReport.
 */
export function deriveGlobalEmgFindingsFromNerveRecords(
  records: EmgNerveRecordLike[],
  ncsResults?: NCSTestResult[],
  opts?: { patientAge?: number; skinTemperatureC?: number }
): DerivedEmgGlobalSlice {
  if (!records?.length) {
    return { overallPattern: 'undetermined', severity: 'undetermined' };
  }

  const physicalFactors =
    opts?.patientAge !== undefined || opts?.skinTemperatureC !== undefined
      ? {
          age: opts?.patientAge ?? 35,
          temperature: opts?.skinTemperatureC ?? 32,
        }
      : undefined;

  let maxSev: DerivedEmgGlobalSlice['severity'] = 'undetermined';
  let myopathicWeight = 0;
  let neuropathicWeight = 0;

  for (const r of records) {
    const unified = emgNerveRecordToUnified(r);
    const patterns = ClinicalEmgPhysiology.analyzeForSpecificPatterns(
      unified,
      physicalFactors,
      ncsResults
    );
    if (!patterns.length) continue;

    const top = patterns[0];
    const topSev = mapPhysiologySeverityToGlobal(top.severity);
    if (severityRankGlobal(topSev) > severityRankGlobal(maxSev)) maxSev = topSev;

    const nameLower = top.name.toLowerCase();
    if (nameLower.includes('miopático')) myopathicWeight += top.score;
    else {
      myopathicWeight += patterns
        .filter(p => p.name.toLowerCase().includes('miopático'))
        .reduce((a, p) => a + p.score * 0.35, 0);
    }

    neuropathicWeight += patterns
      .filter(p => {
        const n = p.name.toLowerCase();
        return (
          n.includes('neuropático') ||
          n.includes('axonal') ||
          n.includes('desmielinizante') ||
          n.includes('alertas neuroconducción')
        );
      })
      .reduce((a, p) => a + p.score, 0);
  }

  const threshold = 0.28;
  let overallPattern: DerivedEmgGlobalSlice['overallPattern'] = 'undetermined';

  if (myopathicWeight < threshold && neuropathicWeight < threshold) {
    overallPattern = maxSev === 'undetermined' ? 'normal' : 'undetermined';
    if (overallPattern === 'undetermined' && maxSev !== 'undetermined') overallPattern = 'neuropathic';
  } else if (myopathicWeight > neuropathicWeight * 1.2 && myopathicWeight >= threshold) {
    overallPattern = 'myopathic';
  } else if (neuropathicWeight > myopathicWeight * 1.2 && neuropathicWeight >= threshold) {
    overallPattern = 'neuropathic';
  } else if (myopathicWeight >= threshold && neuropathicWeight >= threshold) {
    overallPattern = 'mixed';
  } else if (neuropathicWeight >= threshold) {
    overallPattern = 'neuropathic';
  } else if (myopathicWeight >= threshold) {
    overallPattern = 'myopathic';
  }

  if (overallPattern === 'undetermined' && maxSev !== 'undetermined') {
    overallPattern = neuropathicWeight >= myopathicWeight ? 'neuropathic' : 'myopathic';
  }

  return { overallPattern, severity: maxSev };
}
